import React, { useState } from 'react';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { toast } from '@/design-system/toaster';
import { appendCodProofs, type CodLegType } from '@/services/api/codOrders';

import { CodProofPicker, useProofPicker } from './CodProofPicker';

const LEG_LABEL: Record<CodLegType, string> = {
  rider_to_pic: 'Leg 1 · Rider → PIC',
  pic_to_finance: 'Leg 2 · PIC → Finance',
};

/**
 * `+ Tambah bukti` — attach evidence to a leg that already closed.
 *
 * Its own modal, not a mode of the confirm form, because it shares nothing with
 * one: no amount, no method, no discrepancy, no reason. What it does share is
 * the attachment field, which is the part that was worth extracting.
 *
 * Add-only, so the stored list is shown but not removable here. Pruning belongs
 * to the correction form, where the operator sees the figures they are also
 * restating.
 */
const AddProofModal: React.FC<{
  shipmentId: string;
  legType: CodLegType;
  onClose: () => void;
  onDone: () => void;
}> = ({ shipmentId, legType, onClose, onDone }) => {
  const picker = useProofPicker();
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const proofFiles = await picker.upload();
      const result = await appendCodProofs(shipmentId, legType, proofFiles);
      toast.success(
        result.added === 0
          ? 'Lampiran itu sudah ada, tidak ada yang ditambahkan.'
          : `${result.added} lampiran ditambahkan.`
      );
      onDone();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open
      title="Tambah Bukti"
      subtitle={LEG_LABEL[legType]}
      size="lg"
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            style="stroke"
            tone="neutral"
            onClick={onClose}
            disabled={submitting}
          >
            Batal
          </Button>
          {/* Nothing queued means nothing to do: an empty append is a mistake,
              not a request to clear the list. */}
          <Button
            onClick={submit}
            disabled={picker.isEmpty || submitting || picker.uploading}
          >
            {picker.uploading
              ? 'Mengunggah…'
              : submitting
                ? 'Menyimpan…'
                : 'Tambah'}
          </Button>
        </div>
      }
    >
      <CodProofPicker
        picker={picker}
        required
        hint={
          <Hint>
            Bukti bersifat tambah-terus — yang lama tidak terhapus. Untuk
            menghapus, pakai Koreksi.
          </Hint>
        }
      />
    </FormModal>
  );
};

export default AddProofModal;
